
import React, { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import NursingDashboard from '@/components/nursing/NursingDashboard';
import ClockInOut from '@/components/nursing/ClockInOut';
import Patients from '@/components/nursing/Patients';
import PhysicalAssessmentForm from '@/components/nursing/PhysicalAssessmentForm';
import MobileNavigation from '@/components/nursing/MobileNavigation';
import { Onboarding } from '@/components/Onboarding';


const Dashboard = () => {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [showOnboarding, setShowOnboarding] = useState(() => {
    // Check if user has completed onboarding
    return !localStorage.getItem('pcns_onboarding_complete');
  });

  // Sample visit data - in real app this would come from your data store
  const currentVisit = {
    id: 'sample-visit-id',
    patient_id: 'sample-patient-id'
  };

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      toast.error('Error signing out');
    } else {
      toast.success('Signed out successfully');
    }
  };

  const handlePageChange = (page: string) => {
    setCurrentPage(page);
    if (page === 'assessment') {
      navigate('/assessment');
    } else if (page === 'clock') {
      navigate('/clock');
    }
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    localStorage.setItem('pcns_onboarding_complete', 'true');
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <NursingDashboard />;
      case 'clock':
        return <ClockInOut />;
      case 'patients':
        return <Patients />;
      case 'assessment':
        return (
          <PhysicalAssessmentForm 
            visitId={currentVisit.id}
            patientId={currentVisit.patient_id}
            onSave={() => {
              toast.success('Assessment completed!');
              setCurrentPage('dashboard');
            }}
          />
        );
      default:
        return <NursingDashboard />;
    }
  };

  // Show onboarding if needed
  if (showOnboarding) {
    return <Onboarding onComplete={handleOnboardingComplete} />;
  }

  const isAdmin = profile?.role === 'admin';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-gray-900">
              PCNS Connect
            </h1>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600 flex items-center">
                {profile?.full_name}
                {isAdmin && (
                  <span className="ml-2 px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                    Admin
                  </span>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSignOut}
                className="text-gray-600 hover:text-gray-900"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pb-20 md:pb-0">
        {renderPage()}
      </main>

      {/* Mobile Navigation */}
      <MobileNavigation 
        currentPage={currentPage} 
        onPageChange={handlePageChange} 
      />
    </div>
  );
};

export default Dashboard;
